import { ApiError, convertObjectToJson, HOUR } from '@gorila-shared-ui/components';
import { del, get, post } from 'aws-amplify/api';
import { downloadData, getUrl, uploadData } from 'aws-amplify/storage';
import { API_ENDPOINT_NAMES } from '../constants/api';
import { environment } from '../environments/environment';
import { ApiEndpoint } from '../types/api';
import { getErrorMessage } from '../utils/apiErrorResponse';

export const getRequest = async (
  path: string,
  params = {},
  dataType: 'json' | 'text' = 'json',
  endpoint: ApiEndpoint = ApiEndpoint.client,
  acceptEmptyResponse = false,
): Promise<{ data?: any; error?: ApiError }> => {
  try {
    const response = await get({
      apiName: API_ENDPOINT_NAMES[endpoint],
      path,
      options: {
        ...convertObjectToJson(params),
      },
    }).response;
    try {
      return {
        data: await (dataType === 'text' ? response.body.text() : response.body.json()),
      };
    } catch (error) {
      if (acceptEmptyResponse) {
        return { data: undefined };
      }
      throw error;
    }
  } catch (error) {
    return {
      error: getErrorMessage(error, { params, path }),
    };
  }
};

export const getOpenRequest = async (path: string, params = {}): Promise<{ data?: any; error?: ApiError }> => {
  try {
    const response = await get({
      apiName: environment.awsOpenAPIEndpointName,
      path,
      options: {
        ...convertObjectToJson(params),
        headers: {
          'x-api-key': `${environment.awsOpenAPIKey}`,
        },
      },
    }).response;
    return {
      data: await response.body.json(),
    };
  } catch (error) {
    return {
      error: getErrorMessage(error, { params, path }),
    };
  }
};

export const postRequest = async (
  path: string,
  params = {},
  endpoint: ApiEndpoint = ApiEndpoint.client,
): Promise<{ data?: any; error?: ApiError }> => {
  try {
    const response = await post({
      apiName: API_ENDPOINT_NAMES[endpoint],
      path,
      options: {
        ...convertObjectToJson(params),
      },
    }).response;
    try {
      return {
        data: await response.body?.json(),
      };
    } catch {
      return {
        data: undefined,
      };
    }
  } catch (error) {
    return {
      error: getErrorMessage(error, { params, path }),
    };
  }
};

export const fileRequest = async (path: string, doesNotExpire: boolean = false) => {
  try {
    return {
      data: {
        url: (
          await getUrl({
            path,
            options: doesNotExpire
              ? {
                  expiresIn: HOUR * 24,
                }
              : undefined,
          })
        ).url.href,
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
};

export const externalFileRequest = async (
  path: string,
  bucketName: string = environment.awsNewMediaS3Bucket ?? '',
  region: string = environment.awsNewMediaS3BucketRegion,
  doesNotExpire: boolean = false,
) => {
  try {
    return {
      data: {
        url: (
          await getUrl({
            path,
            options: {
              bucket: {
                bucketName,
                region,
              },
              expiresIn: doesNotExpire ? HOUR * 24 : undefined,
            },
          })
        ).url.href,
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
};

export const uploadRequest = async (path: string, file: File, options = {}) => {
  try {
    return {
      data: {
        key: (await uploadData({ path, data: file, options }).result).path,
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
};

export const downloadRequest = async (path: string) => {
  try {
    return {
      data: {
        file: await (await downloadData({ path }).result).body.blob(),
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
};

export const externalDownloadRequest = async (
  path: string,
  bucketName: string = environment.awsNewMediaS3Bucket ?? '',
  region: string = environment.awsNewMediaS3BucketRegion,
) => {
  try {
    return {
      data: {
        file: await (
          await downloadData({
            path,
            options: {
              bucket: {
                bucketName,
                region,
              },
            },
          }).result
        ).body.blob(),
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
};

export const deleteRequest = async (
  path: string,
  params = {},
  endpoint: ApiEndpoint = ApiEndpoint.client,
): Promise<{ data?: any; error?: ApiError }> => {
  try {
    return {
      data: await del({
        apiName: API_ENDPOINT_NAMES[endpoint],
        path,
        options: {
          ...convertObjectToJson(params),
        },
      }).response,
    };
  } catch (error) {
    return {
      error: getErrorMessage(error, { params, path }),
    };
  }
};
